// src/features/organisation/components/employees/RoleCompetenceManager.tsx
import React, { useState, useEffect } from 'react';
import { Employee } from '../../../../types/organisation/employees.types';
import { useRoles } from '../../hooks/useRoles';
import { useSites } from '../../hooks/useSites';
import {
  Plus,
  Trash2,
  Users,
  Award,
  Shield,
  AlertTriangle,
  Calendar,
  Building2,
  X
} from 'lucide-react';

interface RoleCompetenceManagerProps {
  employee: Employee;
  onUpdate?: (employee: Employee) => void;
  onRoleChange?: (change: PendingRoleChange) => void;
  onCompetenceChange?: (change: PendingCompetenceChange) => void;
  pendingRoleChanges?: PendingRoleChange[];
  pendingCompetenceChanges?: PendingCompetenceChange[];
  showOnly?: 'roles' | 'competences';
}

interface NewRoleAssignment {
  roleId: number;
  siteId?: number;
}

interface NewCompetence {
  competenceName: string;
  competenceLevel: string;
  certificationDate: string;
  expiryDate: string;
  certifyingBody: string;
}

interface PendingRoleChange {
  type: 'add' | 'remove' | 'modify';
  id?: number;
  data?: {
    roleId: number;
    siteId?: number;
  };
}

interface PendingCompetenceChange {
  type: 'add' | 'remove' | 'modify';
  id?: number;
  data?: {
    competenceName: string;
    competenceLevel?: string;
    certificationDate?: string;
    expiryDate?: string;
    certifyingBody?: string;
  };
}

const RoleCompetenceManager: React.FC<RoleCompetenceManagerProps> = ({
  employee,
  onRoleChange,
  onCompetenceChange,
  showOnly
}) => {
  const { roles, loading: rolesLoading } = useRoles();
  const { sites, loading: sitesLoading } = useSites();

  // NOUVEAU: État local pour l'employé
  const [currentEmployee, setCurrentEmployee] = useState<Employee>(employee);

  const [showAddRole, setShowAddRole] = useState(false);
  const [showAddCompetence, setShowAddCompetence] = useState(false);
  
  // États pour l'édition inline
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editingCompetenceId, setEditingCompetenceId] = useState<number | null>(null);
  const [editingRoleData, setEditingRoleData] = useState<{ roleId: number; siteId?: number } | null>(null);
  const [editingCompetenceData, setEditingCompetenceData] = useState<{
    competenceName: string;
    competenceLevel?: string;
    certificationDate?: string;
    expiryDate?: string;
    certifyingBody?: string;
  } | null>(null);

  const [newRole, setNewRole] = useState<NewRoleAssignment>({
    roleId: 0,
    siteId: undefined
  });

  const [newCompetence, setNewCompetence] = useState<NewCompetence>({
    competenceName: '',
    competenceLevel: '',
    certificationDate: '',
    expiryDate: '',
    certifyingBody: ''
  });

  // NOUVEAU: Synchroniser avec les props
  useEffect(() => {
    setCurrentEmployee(employee);
  }, [employee.id]); // ✅ Uniquement l'ID

  // Note: refreshEmployee supprimé car les changements sont maintenant en attente

  // Fonctions pour l'édition des rôles
  const handleEditRole = (assignmentId: number) => {
    const assignment = currentEmployee.roleAssignments?.find(ra => ra.id === assignmentId);
    if (assignment) {
      setEditingRoleId(assignmentId);
      setEditingRoleData({
        roleId: assignment.role.id,
        siteId: assignment.site?.id
      });
    }
  };

  const handleSaveRoleEdit = () => {
    if (!editingRoleId || !editingRoleData) return;

    // Créer un changement en attente pour la modification
    const pendingChange: PendingRoleChange = {
      type: 'modify',
      id: editingRoleId,
      data: {
        roleId: editingRoleData.roleId,
        siteId: editingRoleData.siteId
      }
    };

    // Notifier le parent avec le changement de modification
    if (onRoleChange) {
      onRoleChange(pendingChange);
    }

    // Mettre à jour l'affichage local
    const selectedRole = roles?.find(role => role.id === editingRoleData.roleId);
    const selectedSite = sites?.find(site => site.id === editingRoleData.siteId);

    if (selectedRole) {
      setCurrentEmployee(prev => {
        const updatedAssignments = (prev.roleAssignments || []).map(assignment => {
          if (assignment.id === editingRoleId) {
            return {
              ...assignment,
              role: {
                ...selectedRole,
                isManagerRole: false
              },
              site: selectedSite ? { ...selectedSite, id: selectedSite.id } : undefined
            };
          }
          return assignment;
        });

        return {
          ...prev,
          roleAssignments: updatedAssignments
        };
      });
    }

    // Réinitialiser l'édition
    setEditingRoleId(null);
    setEditingRoleData(null);
  };

  const handleCancelRoleEdit = () => {
    setEditingRoleId(null);
    setEditingRoleData(null);
  };

  // Fonctions pour l'édition des compétences
  const handleEditCompetence = (competenceId: number) => {
    const competence = currentEmployee.competences?.find(c => c.id === competenceId);
    if (competence) {
      setEditingCompetenceId(competenceId);
      setEditingCompetenceData({
        competenceName: competence.competenceName,
        competenceLevel: competence.competenceLevel || undefined,
        certificationDate: competence.certificationDate || undefined,
        expiryDate: competence.expiryDate || undefined,
        certifyingBody: competence.certifyingBody || undefined
      });
    }
  };

  const handleSaveCompetenceEdit = () => {
    if (!editingCompetenceId || !editingCompetenceData) return;

    // Créer un changement en attente pour la modification
    const pendingChange: PendingCompetenceChange = {
      type: 'modify',
      id: editingCompetenceId,
      data: {
        competenceName: editingCompetenceData.competenceName,
        competenceLevel: editingCompetenceData.competenceLevel,
        certificationDate: editingCompetenceData.certificationDate,
        expiryDate: editingCompetenceData.expiryDate,
        certifyingBody: editingCompetenceData.certifyingBody
      }
    };

    // Notifier le parent avec le changement de modification
    if (onCompetenceChange) {
      onCompetenceChange(pendingChange);
    }

    // Mettre à jour l'affichage local
    setCurrentEmployee(prev => {
      const updatedCompetences = (prev.competences || []).map(competence => {
        if (competence.id === editingCompetenceId) {
          return {
            ...competence,
            competenceName: editingCompetenceData.competenceName,
            competenceLevel: editingCompetenceData.competenceLevel || undefined,
            certificationDate: editingCompetenceData.certificationDate || undefined,
            expiryDate: editingCompetenceData.expiryDate || undefined,
            certifyingBody: editingCompetenceData.certifyingBody || undefined
          };
        }
        return competence;
      });

      return {
        ...prev,
        competences: updatedCompetences
      };
    });

    // Réinitialiser l'édition
    setEditingCompetenceId(null);
    setEditingCompetenceData(null);
  };

  const handleCancelCompetenceEdit = () => {
    setEditingCompetenceId(null);
    setEditingCompetenceData(null);
  };

  // Ajouter un rôle (en attente)
  const handleAddRole = () => {
    if (!newRole.roleId) {
      alert('Veuillez sélectionner un rôle');
      return;
    }


    // Trouver le rôle sélectionné
    const selectedRole = roles?.find(role => role.id === newRole.roleId);
    const selectedSite = sites?.find(site => site.id === newRole.siteId);

    if (!selectedRole) {
      alert('Rôle non trouvé');
      return;
    }

    // Créer un changement en attente
    const pendingChange: PendingRoleChange = {
      type: 'add',
      data: {
        roleId: newRole.roleId,
        siteId: newRole.siteId
      }
    };

    // Notifier le parent
    if (onRoleChange) {
      onRoleChange(pendingChange);
    }

    // Mettre à jour l'état local pour l'affichage immédiat
    const newAssignment = {
      id: Date.now(), // ID temporaire
      employee: { 
        id: employee.id, 
        employeeCode: employee.employeeCode,
        firstName: employee.firstName,
        lastName: employee.lastName,
        fullName: employee.fullName,
        email: employee.email,
        isQhseResponsible: employee.isQhseResponsible
      },
      role: {
        ...selectedRole,
        isManagerRole: false // Valeur par défaut
      },
      site: selectedSite ? { ...selectedSite, id: selectedSite.id } : undefined,
      assignedAt: new Date().toISOString(),
      assignedBy: undefined,
      isActive: true
    };

    setCurrentEmployee(prev => ({
      ...prev,
      roleAssignments: [...(prev.roleAssignments || []), newAssignment]
    }));

        // Réinitialiser le formulaire
        setShowAddRole(false);
        setNewRole({ roleId: 0, siteId: undefined });
  };

  // Supprimer un rôle (en attente)
  const handleRemoveRole = (assignmentId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette attribution de rôle ?')) {
      return;
    }


    // Créer un changement en attente
    const pendingChange: PendingRoleChange = {
      type: 'remove',
      id: assignmentId
    };

    // Notifier le parent
    if (onRoleChange) {
      onRoleChange(pendingChange);
    }

    // Mettre à jour l'état local pour l'affichage immédiat
    setCurrentEmployee(prev => ({
      ...prev,
      roleAssignments: (prev.roleAssignments || []).filter(assignment => assignment.id !== assignmentId)
    }));

  };

  // Ajouter une compétence (en attente)
  const handleAddCompetence = () => {
    if (!newCompetence.competenceName.trim()) {
      alert('Veuillez saisir un nom de compétence');
      return;
    }

    // Créer un changement en attente
    const pendingChange: PendingCompetenceChange = {
      type: 'add',
      data: {
            competenceName: newCompetence.competenceName,
        competenceLevel: newCompetence.competenceLevel || undefined,
        certificationDate: newCompetence.certificationDate || undefined,
        expiryDate: newCompetence.expiryDate || undefined,
        certifyingBody: newCompetence.certifyingBody || undefined
      }
    };

    // Notifier le parent
    if (onCompetenceChange) {
      onCompetenceChange(pendingChange);
    }

    // Mettre à jour l'état local pour l'affichage immédiat
    const newCompetenceData = {
      id: Date.now(), // ID temporaire
      competenceName: newCompetence.competenceName,
      competenceLevel: newCompetence.competenceLevel || undefined,
      certificationDate: newCompetence.certificationDate || undefined,
      expiryDate: newCompetence.expiryDate || undefined,
      certifyingBody: newCompetence.certifyingBody || undefined,
      isActive: true,
      isExpired: false,
      isExpiringSoon: false,
      createdAt: new Date().toISOString()
    };

    setCurrentEmployee(prev => ({
      ...prev,
      competences: [...(prev.competences || []), newCompetenceData]
    }));

        // Réinitialiser le formulaire
        setShowAddCompetence(false);
        setNewCompetence({
          competenceName: '',
          competenceLevel: '',
          certificationDate: '',
          expiryDate: '',
          certifyingBody: ''
        });

  };

  // Supprimer une compétence (en attente)
  const handleRemoveCompetence = (competenceId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette compétence ?')) {
      return;
    }


    // Créer un changement en attente
    const pendingChange: PendingCompetenceChange = {
      type: 'remove',
      id: competenceId
    };

    // Notifier le parent
    if (onCompetenceChange) {
      onCompetenceChange(pendingChange);
    }

    // Mettre à jour l'état local pour l'affichage immédiat
    setCurrentEmployee(prev => ({
      ...prev,
      competences: (prev.competences || []).filter(competence => competence.id !== competenceId)
    }));

  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const hasRoles = Array.isArray(roles) && roles.length > 0;
  const hasSites = Array.isArray(sites) && sites.length > 0;

  return (
    <div className="space-y-6">
      {/* Section Rôles - UTILISER currentEmployee */}
      {(!showOnly || showOnly === 'roles') && (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Rôles attribués ({currentEmployee.roleAssignments?.length || 0})
          </h3>
          <button
            onClick={() => setShowAddRole(true)}
            className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
            disabled={rolesLoading}
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter</span>
          </button>
        </div>

        {/* Debug: Affichage de l'état de chargement */}
        {rolesLoading && (
          <div className="text-center py-4 text-gray-500">
            Chargement des rôles disponibles...
          </div>
        )}

        {!rolesLoading && !hasRoles && (
          <div className="text-center py-4 text-orange-500">
            Aucun rôle disponible. Vérifiez l'onglet Rôles.
          </div>
        )}

        {!rolesLoading && hasRoles && (
          <div className="text-xs text-green-600 mb-2">
            {roles.length} rôle(s) disponible(s)
          </div>
        )}

        {/* Liste des rôles - UTILISER currentEmployee */}
        <div className="space-y-2">
          {currentEmployee.roleAssignments?.map(assignment => (
            <div key={assignment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              {editingRoleId === assignment.id ? (
                // Mode édition
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="form-label text-xs">Rôle</label>
                      <select
                        value={editingRoleData?.roleId || 0}
                        onChange={(e) => setEditingRoleData(prev => prev ? { ...prev, roleId: parseInt(e.target.value) } : null)}
                        className="form-input text-sm"
                      >
                        <option value={0}>Sélectionner un rôle</option>
                        {roles?.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.roleName} ({role.roleCode})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label text-xs">Site</label>
                      <select
                        value={editingRoleData?.siteId || ''}
                        onChange={(e) => setEditingRoleData(prev => prev ? { ...prev, siteId: e.target.value ? parseInt(e.target.value) : undefined } : null)}
                        className="form-input text-sm"
                      >
                        <option value="">Tous les sites</option>
                        {sites?.map(site => (
                          <option key={site.id} value={site.id}>
                            {site.siteName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={handleCancelRoleEdit}
                      className="btn-secondary text-xs px-3 py-1"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleSaveRoleEdit}
                      disabled={!editingRoleData?.roleId}
                      className="btn-primary text-xs px-3 py-1"
                    >
                      Sauvegarder
                    </button>
                  </div>
                </div>
              ) : (
                // Mode affichage
                <>
                  <div 
                    className="flex items-center space-x-3 flex-1 cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors"
                    onDoubleClick={() => handleEditRole(assignment.id)}
                    title="Double-cliquez pour modifier"
                  >
                {assignment.role.isQhseRole && (
                  <Shield className="h-4 w-4 text-blue-600" />
                )}
                <div>
                  <div className="font-medium text-sm">{assignment.role.roleName}</div>
                  <div className="text-xs text-gray-500">
                    Code: {assignment.role.roleCode}
                  </div>
                  {assignment.site && (
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <Building2 className="h-3 w-3 mr-1" />
                      Site: {assignment.site.siteName}
                    </div>
                  )}
                  <div className="flex items-center text-xs text-gray-500 mt-1">
                    <Calendar className="h-3 w-3 mr-1" />
                    Attribué le: {formatDate(assignment.assignedAt)}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleRemoveRole(assignment.id)}
                className="text-red-600 hover:text-red-800"
                    disabled={false}
              >
                <Trash2 className="h-4 w-4" />
              </button>
                </>
              )}
            </div>
          ))}

          {(!currentEmployee.roleAssignments || currentEmployee.roleAssignments.length === 0) && (
            <div className="text-center py-6 text-gray-500">
              Aucun rôle attribué
            </div>
          )}
        </div>

        {/* Formulaire d'ajout de rôle - INCHANGÉ */}
        {showAddRole && (
          <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50 fade-in">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">Ajouter un rôle</h4>
              <button
                onClick={() => setShowAddRole(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Rôle *
                </label>
                <select
                  value={newRole.roleId}
                  onChange={(e) => setNewRole(prev => ({ ...prev, roleId: parseInt(e.target.value) }))}
                  className="form-input"
                  disabled={!hasRoles}
                >
                  <option value={0}>Sélectionner un rôle</option>
                  {hasRoles && roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.roleName} ({role.roleCode})
                    </option>
                  ))}
                </select>
                {!hasRoles && !rolesLoading && (
                  <div className="text-xs text-orange-600 mt-1">
                    Aucun rôle disponible - Vérifiez l'onglet Rôles
                  </div>
                )}
                {rolesLoading && (
                  <div className="text-xs text-blue-600 mt-1">
                    Chargement des rôles...
                  </div>
                )}
              </div>

              <div>
                <label className="form-label">
                  Site (optionnel)
                </label>
                <select
                  value={newRole.siteId || ''}
                  onChange={(e) => setNewRole(prev => ({ ...prev, siteId: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="form-input"
                  disabled={!hasSites}
                >
                  <option value="">Tous les sites</option>
                  {hasSites && sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.siteName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowAddRole(false)}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleAddRole}
                disabled={!newRole.roleId || !hasRoles}
                className="btn-primary"
              >
                Ajouter
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Section Compétences - UTILISER currentEmployee */}
      {(!showOnly || showOnly === 'competences') && (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center">
            <Award className="h-5 w-5 mr-2" />
            Compétences ({currentEmployee.competences?.length || 0})
          </h3>
          <button
            onClick={() => setShowAddCompetence(true)}
            className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
            disabled={false}
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter</span>
          </button>
        </div>

        {/* Liste des compétences - UTILISER currentEmployee */}
        <div className="space-y-2">
          {currentEmployee.competences?.map(competence => (
            <div key={competence.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              {editingCompetenceId === competence.id ? (
                // Mode édition
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="form-label text-xs">Nom de la compétence *</label>
                      <input
                        type="text"
                        value={editingCompetenceData?.competenceName || ''}
                        onChange={(e) => setEditingCompetenceData(prev => prev ? { ...prev, competenceName: e.target.value } : null)}
                        className="form-input text-sm"
                        placeholder="Ex: Audit ISO 9001"
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Niveau</label>
                      <select
                        value={editingCompetenceData?.competenceLevel || ''}
                        onChange={(e) => setEditingCompetenceData(prev => prev ? { ...prev, competenceLevel: e.target.value } : null)}
                        className="form-input text-sm"
                      >
                        <option value="">Sélectionner un niveau</option>
                        <option value="Débutant">Débutant</option>
                        <option value="Confirmé">Confirmé</option>
                        <option value="Expert">Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label text-xs">Date de certification</label>
                      <input
                        type="date"
                        value={editingCompetenceData?.certificationDate || ''}
                        onChange={(e) => setEditingCompetenceData(prev => prev ? { ...prev, certificationDate: e.target.value } : null)}
                        className="form-input text-sm"
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Date d'expiration</label>
                      <input
                        type="date"
                        value={editingCompetenceData?.expiryDate || ''}
                        onChange={(e) => setEditingCompetenceData(prev => prev ? { ...prev, expiryDate: e.target.value } : null)}
                        className="form-input text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label text-xs">Organisme certificateur</label>
                      <input
                        type="text"
                        value={editingCompetenceData?.certifyingBody || ''}
                        onChange={(e) => setEditingCompetenceData(prev => prev ? { ...prev, certifyingBody: e.target.value } : null)}
                        className="form-input text-sm"
                        placeholder="Ex: Bureau Veritas"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={handleCancelCompetenceEdit}
                      className="btn-secondary text-xs px-3 py-1"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleSaveCompetenceEdit}
                      disabled={!editingCompetenceData?.competenceName.trim()}
                      className="btn-primary text-xs px-3 py-1"
                    >
                      Sauvegarder
                    </button>
                  </div>
                </div>
              ) : (
                // Mode affichage
                <>
                  <div 
                    className="flex-1 cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors"
                    onDoubleClick={() => handleEditCompetence(competence.id)}
                    title="Double-cliquez pour modifier"
                  >
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-sm">{competence.competenceName}</span>
                  {competence.isExpired && (
                    <span className="status-badge status-error">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Expirée
                    </span>
                  )}
                  {competence.isExpiringSoon && !competence.isExpired && (
                    <span className="status-badge status-warning">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Expire bientôt
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs text-gray-500">
                  {competence.competenceLevel && (
                    <div>Niveau: {competence.competenceLevel}</div>
                  )}
                  {competence.certificationDate && (
                    <div>Certifié le: {formatDate(competence.certificationDate)}</div>
                  )}
                  {competence.expiryDate && (
                    <div className={competence.isExpired ? 'text-red-600' : competence.isExpiringSoon ? 'text-orange-600' : ''}>
                      Expire le: {formatDate(competence.expiryDate)}
                    </div>
                  )}
                  {competence.certifyingBody && (
                    <div>Organisme: {competence.certifyingBody}</div>
                  )}
                </div>
              </div>

              <button
                onClick={() => handleRemoveCompetence(competence.id)}
                className="text-red-600 hover:text-red-800 ml-2"
                    disabled={false}
              >
                <Trash2 className="h-4 w-4" />
              </button>
                </>
              )}
            </div>
          ))}

          {(!currentEmployee.competences || currentEmployee.competences.length === 0) && (
            <div className="text-center py-6 text-gray-500">
              Aucune compétence enregistrée
            </div>
          )}
        </div>

        {/* Formulaire d'ajout de compétence - INCHANGÉ */}
        {showAddCompetence && (
          <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50 fade-in">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">Ajouter une compétence</h4>
              <button
                onClick={() => setShowAddCompetence(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Nom de la compétence *
                </label>
                <input
                  type="text"
                  value={newCompetence.competenceName}
                  onChange={(e) => setNewCompetence(prev => ({ ...prev, competenceName: e.target.value }))}
                  className="form-input"
                  placeholder="Ex: Audit ISO 9001"
                />
              </div>

              <div>
                <label className="form-label">
                  Niveau
                </label>
                <select
                  value={newCompetence.competenceLevel}
                  onChange={(e) => setNewCompetence(prev => ({ ...prev, competenceLevel: e.target.value }))}
                  className="form-input"
                >
                  <option value="">Sélectionner un niveau</option>
                  <option value="Débutant">Débutant</option>
                  <option value="Confirmé">Confirmé</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>

              <div>
                <label className="form-label">
                  Date de certification
                </label>
                <input
                  type="date"
                  value={newCompetence.certificationDate}
                  onChange={(e) => setNewCompetence(prev => ({ ...prev, certificationDate: e.target.value }))}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">
                  Date d'expiration
                </label>
                <input
                  type="date"
                  value={newCompetence.expiryDate}
                  onChange={(e) => setNewCompetence(prev => ({ ...prev, expiryDate: e.target.value }))}
                  className="form-input"
                />
              </div>

              <div className="md:col-span-2">
                <label className="form-label">
                  Organisme certificateur
                </label>
                <input
                  type="text"
                  value={newCompetence.certifyingBody}
                  onChange={(e) => setNewCompetence(prev => ({ ...prev, certifyingBody: e.target.value }))}
                  className="form-input"
                  placeholder="Ex: Bureau Veritas"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowAddCompetence(false)}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleAddCompetence}
                disabled={!newCompetence.competenceName.trim()}
                className="btn-primary"
              >
                Ajouter
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default RoleCompetenceManager;